import * as Yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";

import BaseInput from "../../../components/elements/BaseInput";
import Button from "../../../components/elements/Button";
import FormFieldError from "../../../components/elements/FormFieldError";
import api from "../../../api";
import classes from "./CreateSegmentModal.module.scss";
import { useGlobalSelectedAdvertiser } from "../../../hooks/useGlobalSelectedAdvertiser";

type CreateAdvertiserModalProps = {
  isOpen: boolean;
  shouldSetDefaultAdvertiser: boolean;
  handleClose: () => void;
};

type FormInitialValues = {
  name: string;
  description: string;
  advertiserId: number | null;
};

const createSegment = async (name: string, advertiserId: number) => {
  try {
    await api.createSegment(name, advertiserId);
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const formInitialValues: FormInitialValues = {
  name: "",
  description: "",
  advertiserId: null,
};

const CreateAdvertiserModal: FC<CreateAdvertiserModalProps> = ({ isOpen, handleClose }) => {
  const validationSchema = Yup.object({
    name: Yup.string().required("Required field."),
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { adLibAdvertiserId } = useGlobalSelectedAdvertiser();

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      setIsLoading(true);
      const { name } = values;
      if (!adLibAdvertiserId) {
        setErrorMessage("Please select an advertiser from the profile menu.");
        setIsLoading(false);
        return;
      }
      await createSegment(name, adLibAdvertiserId);
      setIsLoading(false);
      handleClose();
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Segment Creation Modal"
      className={classes.container}
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ values }) => (
          <>
            <div className={classes.dialogTitle}>
              <Typography variant="h2">Create New Re-targeting segment</Typography>
            </div>
            <Form>
              <DialogContent className={classes.dialogContent}>
                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.name}
                    error={!!errorMessage}
                    name="name"
                    variant="outlined"
                    label="Name *"
                  />

                  <FormFieldError name="name" />
                </div>

                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.description}
                    error={!!errorMessage}
                    name="description"
                    variant="outlined"
                    label="Description"
                    multiline
                    rows={3}
                  />

                  <FormFieldError name="description" />
                </div>

                {errorMessage && <Typography color="error">{errorMessage}</Typography>}
              </DialogContent>
              <DialogActions>
                <Button onClick={isLoading ? () => null : handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button type="submit" isLoading={isLoading} loaderSize={25} disabled={!adLibAdvertiserId}>
                  Submit
                </Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateAdvertiserModal;
